import React, { KeyboardEvent, useId } from 'react';

import { InputField } from '../components/InputField';
import { InputFieldClearButton } from '../components/InputFieldClearButton';
import { InputControl } from '../components/InputControl';
import { InputFieldBody } from '../components/InputFieldBody';
import {
  CommonProps,
  ClearButtonFeature,
  CharacterCounterFeature,
} from '../types';
import { Box } from '../../Box';

type TextareaProps = CommonProps &
  ClearButtonFeature &
  CharacterCounterFeature & {
    height?: string;
    onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  };

const Textarea = (props: TextareaProps) => {
  const id = useId();
  const {
    name,
    placeholder,
    error,
    height,
    disabled,
    onKeyDown,
    autoFocus,
    helpText,
    extraHelpText,
    label,
    hasCharacterCounter,
    hasClearButton,
    maxLength,
    validations = {},
    condensed,
    formHooks: { register, watch, resetField },
  } = props;
  const value = watch && watch(name);
  const showClearButton = Boolean(hasClearButton && value);
  const hasLengthError = maxLength ? Number(value?.length) > maxLength : false;
  if (!validations?.maxLength) validations.maxLength = maxLength;

  return (
    <InputControl
      error={error}
      helpText={helpText}
      extraHelpText={extraHelpText}
      value={value}
      hasCharacterCounter={hasCharacterCounter}
      maxLength={maxLength}
      label={label}
      htmlFor={id}
      condensed={condensed}
    >
      <InputField
        disabled={disabled}
        hasError={Boolean(error) || hasLengthError}
      >
        <InputFieldBody
          height={height}
          disabled={disabled}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          placeholder={placeholder}
          register={register}
          id={id}
          name={name}
          validations={validations}
          as="textarea"
        />
        {showClearButton && (
          <Box pt={1} display="flex">
            <InputFieldClearButton
              onClick={() => resetField?.(name)}
              disabled={disabled}
            />
          </Box>
        )}
      </InputField>
    </InputControl>
  );
};

export { Textarea };
